import { useCallback, useState } from 'react';
import {
    Active,
    ClientRect,
    CollisionDetection,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    Over,
    PointerSensor,
    Translate,
    closestCenter,
    closestCorners,
    pointerWithin,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { MoveLeadRequest } from '../services/requests/lead.request';
import { BoardColumnWithLeads } from '../services/responses/board-column.response';
import { BoardDetailResponse } from '../services/responses/board.response';
import { LeadResponse } from '../services/responses/lead.response';
import { apiErrorMessage } from '@functions/api-error-message';
import { DropAction, dragData } from '../drag/crm-drag-data';
import { DropTarget, calculateDropTarget, pastMidpoint } from '../drag/crm-drop-target';

/// Pixels até o arrasto começar. Abaixo disso o gesto é clique, e o card abre.
export const DRAG_DISTANCE = 6;

interface Params {
    board: BoardDetailResponse | null;
    applyOptimisticColumnReorder: (columns: BoardColumnWithLeads[]) => void;
    applyOptimisticLeadMove: (leadId: string, sourceColumnId: string, targetColumnId: string, targetIndex: number) => void;
    reorderColumns: (boardId: string, orderedIds: string[]) => Promise<void>;
    moveLead: (leadId: string, payload: MoveLeadRequest) => Promise<void>;
    reorderLeads: (columnId: string, orderedIds: string[]) => Promise<void>;
    refreshBoard: (boardId: string) => Promise<void>;
    onError: (message: string) => void;
    /// Lead solto numa zona do rodapé; quem chama pergunta o que falta antes de gravar.
    onDropAction: (action: DropAction, lead: LeadResponse) => void;
}

/// Onde a prévia desenha a linha: a coluna e o card que ficará abaixo dela.
export interface DropPreview {
    columnId: string;
    before: string | null;
}

export const useCrmDrag = ({
    board,
    applyOptimisticColumnReorder,
    applyOptimisticLeadMove,
    reorderColumns,
    moveLead,
    reorderLeads,
    refreshBoard,
    onError,
    onDropAction,
}: Params) => {
    const [activeLead, setActiveLead] = useState<LeadResponse | null>(null);
    const [activeColumn, setActiveColumn] = useState<BoardColumnWithLeads | null>(null);
    const [dragSourceColId, setDragSourceColId] = useState<string | null>(null);
    const [preview, setPreview] = useState<DropPreview | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: DRAG_DISTANCE } })
    );

    const collisionDetection: CollisionDetection = useCallback(
        (args) => {
            if (activeColumn) {
                return closestCorners({
                    ...args,
                    droppableContainers: args.droppableContainers.filter(
                        (container) => container.data.current?.type === 'column'
                    ),
                });
            }

            // Zona do rodapé ganha de tudo: fica sobre o fim das colunas e quem chega lá quer a ação.
            // Vale o card encostar, não o cursor: esperar a mão na zona parecia que ela não respondia.
            const actions = rectIntersection({
                ...args,
                droppableContainers: args.droppableContainers.filter(
                    (container) => container.data.current?.type === 'action'
                ),
            });

            if (actions.length > 0) return actions;

            // Coluna sob o cursor primeiro, pois limita a busca: sem isso a coluna vazia nunca recebe.
            const columns = pointerWithin({
                ...args,
                droppableContainers: args.droppableContainers.filter(
                    (container) => container.data.current?.type === 'column'
                ),
            });

            // Sem as zonas: fora de qualquer coluna, o card não cai numa ação só por encostar.
            if (columns.length === 0) {
                return rectIntersection({
                    ...args,
                    droppableContainers: args.droppableContainers.filter(
                        (container) => container.data.current?.type !== 'action'
                    ),
                });
            }

            const columnId = columns[0].id;

            const cards = args.droppableContainers.filter((container) => {
                if (container.data.current?.type !== 'lead') return false;

                // O próprio card não pode ser alvo de si mesmo.
                if (container.id === args.active.id) return false;

                return board?.columns
                    .find((column) => column.leads.some((lead) => lead.id === container.id))
                    ?.id === columnId;
            });

            if (cards.length === 0) return columns;

            const nearest = closestCenter({ ...args, droppableContainers: cards });

            return nearest.length > 0 ? nearest : columns;
        },
        [activeColumn, board]
    );

    /// Onde o ponteiro está: o dnd-kit só dá o deslocamento desde o clique, então soma-se os dois.
    /// É o ponto da mão, não o centro do card arrastado, que se compara com os cards da coluna.
    const pointerY = (event: Event | null, delta: Translate): number | null => {
        const click = event as PointerEvent | null;

        return typeof click?.clientY === 'number' ? click.clientY + delta.y : null;
    };

    /// Traduz o que está sob o cursor em coluna e posição.
    /// Os retângulos são medidos uma vez, ao começar o arraste: por isso nada reordena durante ele.
    const resolveTarget = (
        active: Active,
        over: Over,
        delta: Translate,
        activatorEvent: Event | null
    ): { columnId: string; target: DropTarget } | null => {
        if (!board) return null;

        const overData = dragData(over.data.current);
        let columnId: string;
        let overId: string | null;

        if (overData?.type === 'lead') {
            const cardColumn = board.columns.find((c) => c.leads.some((l) => l.id === over.id));
            if (!cardColumn) return null;

            columnId = cardColumn.id;
            overId = over.id as string;
        } else if (overData?.type === 'column') {
            columnId = over.id as string;
            overId = null;
        } else {
            return null;
        }

        const column = board.columns.find((c) => c.id === columnId);
        if (!column) return null;

        const y = pointerY(activatorEvent, delta);
        const rect = over.rect as ClientRect;

        const afterTarget =
            overId !== null && y !== null
                ? pastMidpoint(y, rect.top, rect.height)
                : false;

        return {
            columnId,
            target: calculateDropTarget({
                columnLeads: column.leads.map((l) => l.id),
                dragged: active.id as string,
                overId,
                afterTarget,
            }),
        };
    };

    const rollback = async (err: unknown) => {
        onError(apiErrorMessage(err));
        if (board) await refreshBoard(board.id);
    };

    const clear = () => {
        setActiveLead(null);
        setActiveColumn(null);
        setDragSourceColId(null);
        setPreview(null);
    };

    const handleDragStart = ({ active }: DragStartEvent) => {
        const data = dragData(active.data.current);
        if (data?.type === 'lead') {
            setActiveLead(data.lead);
            setDragSourceColId(data.lead.columnId);
        } else if (data?.type === 'column') {
            setActiveColumn(data.column);
        }
    };

    const handleDragOver = ({ active, over, delta, activatorEvent }: DragOverEvent) => {
        if (!over) return;
        if (dragData(active.data.current)?.type !== 'lead') return;

        const destination = resolveTarget(active, over, delta, activatorEvent);

        if (!destination) {
            setPreview(null);
            return;
        }

        setPreview((current) =>
            current?.columnId === destination.columnId && current.before === destination.target.before
                ? current
                : { columnId: destination.columnId, before: destination.target.before }
        );
    };

    const handleDragCancel = () => clear();

    const handleDragEnd = async ({ active, over, delta, activatorEvent }: DragEndEvent) => {
        const originColId = dragSourceColId;
        clear();

        if (!over || !board) return;

        const activeData = dragData(active.data.current);

        if (activeData?.type === 'column') {
            const oldIndex = board.columns.findIndex((c) => c.id === active.id);
            const newIndex = board.columns.findIndex((c) => c.id === over.id);
            if (oldIndex === newIndex || newIndex === -1) return;

            const newCols = arrayMove(board.columns, oldIndex, newIndex);
            applyOptimisticColumnReorder(newCols);
            try {
                await reorderColumns(board.id, newCols.map((c) => c.id));
            } catch (err) {
                await rollback(err);
            }
            return;
        }

        if (activeData?.type !== 'lead' || !originColId) return;

        const overData = dragData(over.data.current);

        if (overData?.type === 'action') {
            onDropAction(overData.action, activeData.lead);
            return;
        }

        const destination = resolveTarget(active, over, delta, activatorEvent);
        if (!destination) return;

        const leadId = active.id as string;
        const { columnId, target } = destination;

        if (originColId !== columnId) {
            applyOptimisticLeadMove(leadId, originColId, columnId, target.index);

            try {
                await moveLead(leadId, {
                    targetColumnId: columnId,
                    targetSortOrder: target.index,
                });
                await refreshBoard(board.id);
            } catch (err) {
                await rollback(err);
            }
            return;
        }

        const column = board.columns.find((c) => c.id === columnId);
        const oldIndex = column?.leads.findIndex((l) => l.id === leadId) ?? -1;

        // `indice` já desconta o card arrastado, como o arrayMove; iguais, a soltura não mudou nada.
        if (!column || oldIndex === -1 || oldIndex === target.index) return;

        const newIds = arrayMove(column.leads, oldIndex, target.index).map((l) => l.id);
        applyOptimisticLeadMove(leadId, columnId, columnId, target.index);
        try {
            await reorderLeads(columnId, newIds);
        } catch (err) {
            await rollback(err);
        }
    };

    return {
        sensors,
        collisionDetection,
        activeLead,
        activeColumn,
        preview,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
    };
};
